/* eslint-disable camelcase */
import React, { forwardRef, useImperativeHandle, useEffect } from "react"
import { Grid, TextField, Box, Paper, Typography, Autocomplete, InputAdornment, CircularProgress } from "@mui/material"
import { Domain, LocationOn, Map } from "@mui/icons-material"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useQuery } from "@tanstack/react-query"

import { api } from "../../../../api"

const FormularioRecintoElectoral = forwardRef(({ onSubmit, isLoading, modo, initialData }, ref) => {
  // ========================================================
  // 1. CONFIGURACIÓN DE FORMIK (VALIDACIONES Y ESTADOS)
  // ========================================================
  const formik = useFormik({
    initialValues: {
      reci_codigoOld: "",
      reci_codigo: "",
      reci_nombre: "",
      prov_codigo: "",
      cant_codigo: "",
      parr_codigo: "",
      zona_codigo: "",
      reci_direccion: "",
    },
    validationSchema: Yup.object({
      reci_codigo: Yup.string().required("El código es obligatorio").max(10, "Máximo 10 caracteres"),
      reci_nombre: Yup.string().required("El nombre del recinto es obligatorio").max(150, "Máximo 150 caracteres"),
      prov_codigo: Yup.string().required("Seleccione una provincia"),
      cant_codigo: Yup.string().required("Seleccione un cantón"),
      parr_codigo: Yup.string().required("Seleccione una parroquia"),
      zona_codigo: Yup.string().required("Seleccione una zona"),
    }),
    onSubmit: async (values) => {
      await onSubmit(values)
    },
  })

  useImperativeHandle(ref, () => ({
    handleSubmit: formik.submitForm,
  }))

  useEffect(() => {
    if (modo === "editar" && initialData) {
      formik.setValues({
        reci_codigoOld: initialData.reci_codigo || "",
        reci_codigo: initialData.reci_codigo || "",
        reci_nombre: initialData.reci_nombre || "",
        prov_codigo: initialData.prov_codigo || "",
        cant_codigo: initialData.cant_codigo || "",
        parr_codigo: initialData.parr_codigo || "",
        zona_codigo: initialData.zona_codigo || "",
        reci_direccion: initialData.reci_direccion || "",
      })
    }
  }, [modo, initialData])

  // ========================================================
  // 2. CONSULTAS EN CASCADA (REACT-QUERY)
  // ========================================================

  const { data: rawProvincias = [], isFetching: isFetchingProv } = useQuery({
    queryKey: ["ubicacion_provincias"],
    queryFn: async () => {
      const res = await api.post("/ControlElectoral/getUbicaciones", { tipo: "PROVINCIA" })
      return res.data?.data || []
    },
    staleTime: 5 * 60 * 1000,
  })
  const listaProvincias = Array.isArray(rawProvincias)
    ? rawProvincias.map((i) => ({ id: String(i.id), label: i.label }))
    : []

  const { data: rawCantones = [], isFetching: isFetchingCant } = useQuery({
    queryKey: ["ubicacion_cantones", formik.values.prov_codigo],
    queryFn: async () => {
      const res = await api.post("/ControlElectoral/getUbicaciones", {
        tipo: "CANTON",
        prov_codigo: formik.values.prov_codigo,
      })
      return res.data?.data || []
    },
    enabled: !!formik.values.prov_codigo,
  })
  const listaCantones = Array.isArray(rawCantones) ? rawCantones.map((i) => ({ id: String(i.id), label: i.label })) : []

  const { data: rawParroquias = [], isFetching: isFetchingParr } = useQuery({
    queryKey: ["ubicacion_parroquias", formik.values.prov_codigo, formik.values.cant_codigo],
    queryFn: async () => {
      const res = await api.post("/ControlElectoral/getUbicaciones", {
        tipo: "PARROQUIA",
        prov_codigo: formik.values.prov_codigo,
        cant_codigo: formik.values.cant_codigo,
      })
      return res.data?.data || []
    },
    enabled: !!formik.values.cant_codigo,
  })
  const listaParroquias = Array.isArray(rawParroquias)
    ? rawParroquias.map((i) => ({ id: String(i.id), label: i.label }))
    : []

  const { data: rawZonas = [], isFetching: isFetchingZona } = useQuery({
    queryKey: ["ubicacion_zonas", formik.values.prov_codigo, formik.values.cant_codigo, formik.values.parr_codigo],
    queryFn: async () => {
      const res = await api.post("/ControlElectoral/getUbicaciones", {
        tipo: "ZONA",
        prov_codigo: formik.values.prov_codigo,
        cant_codigo: formik.values.cant_codigo,
        parr_codigo: formik.values.parr_codigo,
      })
      return res.data?.data || []
    },
    enabled: !!formik.values.parr_codigo,
  })
  const listaZonas = Array.isArray(rawZonas) ? rawZonas.map((i) => ({ id: String(i.id), label: i.label })) : []

  // ========================================================
  // RENDERIZADO DEL FORMULARIO
  // ========================================================
  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        {/* SECCIÓN 1: DATOS GENERALES DEL RECINTO */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
            <Typography variant="h6" color="primary" fontWeight="bold" mb={3} display="flex" alignItems="center">
              <Domain sx={{ mr: 1 }} /> Datos del Recinto
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="reci_codigo"
                  name="reci_codigo"
                  label="Código del Recinto *"
                  disabled={modo === "editar" || isLoading}
                  value={formik.values.reci_codigo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.reci_codigo && Boolean(formik.errors.reci_codigo)}
                  helperText={formik.touched.reci_codigo && formik.errors.reci_codigo}
                  inputProps={{ maxLength: 10, style: { textTransform: "uppercase", fontWeight: "bold" } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="reci_nombre"
                  name="reci_nombre"
                  label="Nombre del Establecimiento *"
                  disabled={isLoading}
                  value={formik.values.reci_nombre}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.reci_nombre && Boolean(formik.errors.reci_nombre)}
                  helperText={formik.touched.reci_nombre && formik.errors.reci_nombre}
                  inputProps={{ maxLength: 150 }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  id="reci_direccion"
                  name="reci_direccion"
                  label="Dirección Específica (Opcional)"
                  disabled={isLoading}
                  value={formik.values.reci_direccion}
                  onChange={formik.handleChange}
                  inputProps={{ maxLength: 250 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* SECCIÓN 2: UBICACIÓN GEOGRÁFICA (COMBOS EN CASCADA) */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%", bgcolor: "#f9fbfd" }}>
            <Typography variant="h6" color="secondary.main" fontWeight="bold" mb={3} display="flex" alignItems="center">
              <Map sx={{ mr: 1 }} /> Ubicación Geográfica
            </Typography>

            <Grid container spacing={2}>
              {/* COMBO PROVINCIA */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={listaProvincias}
                  getOptionLabel={(o) => o.label || ""}
                  value={listaProvincias.find((i) => i.id === formik.values.prov_codigo) || null}
                  onChange={(e, newValue) => {
                    formik.setFieldValue("prov_codigo", newValue ? newValue.id : "")
                    formik.setFieldValue("cant_codigo", "")
                    formik.setFieldValue("parr_codigo", "")
                    formik.setFieldValue("zona_codigo", "")
                  }}
                  disabled={isLoading || isFetchingProv}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Provincia *"
                      placeholder={isFetchingProv ? "Cargando..." : "Seleccione..."}
                      error={formik.touched.prov_codigo && Boolean(formik.errors.prov_codigo)}
                      helperText={formik.touched.prov_codigo && formik.errors.prov_codigo}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: isFetchingProv ? (
                          <CircularProgress size={20} />
                        ) : (
                          params.InputProps.startAdornment
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* COMBO CANTÓN */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={listaCantones}
                  getOptionLabel={(o) => o.label || ""}
                  value={listaCantones.find((i) => i.id === formik.values.cant_codigo) || null}
                  onChange={(e, newValue) => {
                    formik.setFieldValue("cant_codigo", newValue ? newValue.id : "")
                    formik.setFieldValue("parr_codigo", "")
                    formik.setFieldValue("zona_codigo", "")
                  }}
                  disabled={isLoading || isFetchingCant || !formik.values.prov_codigo}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cantón *"
                      placeholder={
                        !formik.values.prov_codigo
                          ? "Seleccione provincia"
                          : isFetchingCant
                            ? "Cargando..."
                            : "Seleccione..."
                      }
                      error={formik.touched.cant_codigo && Boolean(formik.errors.cant_codigo)}
                      helperText={formik.touched.cant_codigo && formik.errors.cant_codigo}
                    />
                  )}
                />
              </Grid>

              {/* COMBO PARROQUIA */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={listaParroquias}
                  getOptionLabel={(o) => o.label || ""}
                  value={listaParroquias.find((i) => i.id === formik.values.parr_codigo) || null}
                  onChange={(e, newValue) => {
                    formik.setFieldValue("parr_codigo", newValue ? newValue.id : "")
                    formik.setFieldValue("zona_codigo", "")
                  }}
                  disabled={isLoading || isFetchingParr || !formik.values.cant_codigo}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Parroquia *"
                      placeholder={
                        !formik.values.cant_codigo
                          ? "Seleccione cantón"
                          : isFetchingParr
                            ? "Cargando..."
                            : "Seleccione..."
                      }
                      error={formik.touched.parr_codigo && Boolean(formik.errors.parr_codigo)}
                      helperText={formik.touched.parr_codigo && formik.errors.parr_codigo}
                    />
                  )}
                />
              </Grid>

              {/* COMBO ZONA */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={listaZonas}
                  getOptionLabel={(o) => o.label || ""}
                  value={listaZonas.find((i) => i.id === formik.values.zona_codigo) || null}
                  onChange={(e, newValue) => {
                    formik.setFieldValue("zona_codigo", newValue ? newValue.id : "")
                  }}
                  disabled={isLoading || isFetchingZona || !formik.values.parr_codigo}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Zona Electoral *"
                      placeholder={
                        !formik.values.parr_codigo
                          ? "Seleccione parroquia"
                          : isFetchingZona
                            ? "Cargando..."
                            : "Seleccione..."
                      }
                      error={formik.touched.zona_codigo && Boolean(formik.errors.zona_codigo)}
                      helperText={formik.touched.zona_codigo && formik.errors.zona_codigo}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </form>
  )
})

export default FormularioRecintoElectoral
